import { Chunk } from '@/src/core/streaming/chunk';
import { Tags } from '@/src/core/dicomTags';

function sliceSize(chunk: Chunk): string {
  const meta = Object.fromEntries(chunk.metadata ?? []);
  return [Tags.Rows, Tags.Columns, Tags.SamplesPerPixel]
    .map((tag) => (meta[tag] ?? '').trim())
    .join('x');
}

/**
 * Tách nhóm có ảnh khác kích thước thành từng khối theo kích thước.
 *
 * VolView cấp cả khối theo kích thước lát đầu rồi chép từng lát vào; lát to hơn nằm cuối khối thì
 * tràn ra ngoài ("RangeError: offset is out of bounds"), nằm giữa thì đè lên lát kế bên. GDCM của
 * itk-wasm KHÔNG tách theo Rows/Columns dù đã bật SetUseSeriesDetails.
 *
 * Đo thật ngày 07/10/2026 tại Hồng Phúc: CT United Imaging lưu loạt "axial35" gồm 24 lát 512x512
 * và 1 ảnh màn hình 563x914 (DERIVED\SECONDARY) chung SeriesInstanceUID ⇒ mở ca nào có loạt đó
 * VolView cũng báo lỗi đỏ, các khung MPR sọc.
 *
 * Nhóm đông nhất giữ khoá cũ, các nhóm còn lại thêm hậu tố kích thước và vẫn xem được như khối riêng.
 */
export function splitMixedSizeGroups(
  groups: Record<string, Chunk[]>
): Record<string, Chunk[]> {
  const canTach = Object.values(groups).some(
    (chunks) => new Set(chunks.map(sliceSize)).size > 1
  );
  // Không có gì phải tách thì trả lại đúng đối tượng cũ (nơi gọi so sánh bằng tham chiếu).
  if (!canTach) return groups;

  const ketQua: Record<string, Chunk[]> = {};

  Object.entries(groups).forEach(([khoa, chunks]) => {
    const theoCo = new Map<string, Chunk[]>();
    chunks.forEach((chunk) => {
      const co = sliceSize(chunk);
      theoCo.set(co, [...(theoCo.get(co) ?? []), chunk]);
    });

    if (theoCo.size === 1) {
      ketQua[khoa] = chunks;
      return;
    }

    const dongNhat = [...theoCo.values()].reduce((a, b) =>
      b.length > a.length ? b : a
    );
    theoCo.forEach((nhom, co) => {
      ketQua[nhom === dongNhat ? khoa : `${khoa}&size=${co}`] = nhom;
    });
  });

  return ketQua;
}
